/*
 * Copyright (c) 2023 - 2026 Bluemate B.V.
 */

import {
    AppNavigator,
    AwesomeIcon,
    copyToClipboard,
    directTimeout,
    getMode,
    getSetting,
    Loader,
    remToPx,
    ResponseData,
    showDialog,
    useEvent
} from '@dvrd/dvr-controls';
import {IconName} from '@fortawesome/fontawesome-svg-core';
import {googleLogout, GoogleOAuthProvider} from '@react-oauth/google';
import {Elements} from '@stripe/react-stripe-js';
import {loadStripe} from '@stripe/stripe-js';

import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';
import {Outlet, useLocation, useNavigate} from 'react-router';
import HeaderController from './components/header/headerController';
import APIConnectionBar from './components/dashboard/apiConnectionBar';
import {AppContext} from './components/misc/appContext';
import {LayoutContext} from './components/misc/layoutContext';
import {startIdleObserver, stopIdleObserver} from './components/misc/idleObserver';
import PopupController from './components/misc/popupController';
import OverviewApiUrlSync from './components/misc/overviewApiUrlSync';

import URLActions from './components/misc/urlActions';
import SidebarMenuController from './components/sidebarMenu/sidebarMenuController';

import BottomNavBar from './components/bottomNavBar/bottomNavBar';

import {NotificationBadgeProvider} from './components/misc/notificationBadgeContext';

import {MobileNavProvider} from './components/misc/mobileNavContext';

import BluemateContent from './components/misc/bluemateContent';
import classNames from 'classnames';
import TrialEndController from './components/trialEnd/trialEndController';
import PaywallErrorBoundary from './components/misc/paywallErrorBoundary';

import {useBreakpoint} from './util/useBreakpoint';

import Account from './models/accountModel';
import User from './models/userModel';
import {getDefaultRoute, showsMobileReviewFeed} from './util/accessUtil';

import {hasAdminLogin, onAdminLogout, setIsAdminLogin} from './util/adminUtil';

import {sendGaEvent, setGaUserId} from './util/analytics';
import {onConnectBolOauth} from './util/bolOauthUtil';

import {
    ADMIN_ROUTE,
    ADMIN_USERS_ROUTE,
    CREDIT_DATA_ROUTE,
    DASHBOARD_ROUTE,
    EDIT_INVOICE_ROUTE,
    INVOICE_DETAILS_ROUTE,
    LOGIN_ROUTE,
    CHROME_EXTENSION_ROUTE,
    MCP_SERVER_ROUTE,
    MOBILE_APP_ROUTE,
    MOBILE_READY_ROUTES,
    NOTIFICATIONS_ROUTE,
    ORDERS_ROUTE,
    REGISTER_ROUTE,
    REVIEW_ANALYTICS_ROUTE,
    SELECTED_ACCOUNT_KEY,
    SETUP_ACCOUNT_ROUTE,
    SUBSCRIPTION_ROUTE,
    isBareRoutePath
} from './util/constants';

import {isSafeRedirectPath} from './routes';

import {onAddAccount, onGlobalLoad} from './util/events';
import {clearBeaconIds, setBeaconIds} from './util/helpscoutUtil';
import {requireMaintenanceLogin} from './util/maintenanceUtil';
import {setMenuWidth} from './util/menuUtil';

import {adminLogin as adminLoginRequest, logout, updateUser} from './util/requestSender';

import {setSentryUser} from './util/sentryUtil';
import {onShowConfirmSnackbar} from './util/renderUtil';
import {
    deleteFromAppLocal,
    deleteFromLocal,
    deleteFromSession,
    getFromLocal,
    PUSH_EMAIL_KEY,
    saveToAppLocal,
    saveToLocal,
    setCSRF
} from './util/storageUtil';
import {UserType} from './util/interfaces';
import InfoBanner from './components/misc/infoBanner';

import {BluemateBadge} from './components/controls';

import {
    isStandalone,
    isTouchFirstDevice,
    markPwaInstalledLocally,
    PWA_INSTALL_AVAILABLE_EVENT,
    PWA_INSTALLED_FLEX_KEY,
    registerServiceWorker,
    setupInstallPrompt,
    shouldRefreshPwaInstalledHeartbeat
} from './util/pwaUtil';

import {cleanupUninstalledPwaSubscription} from './util/pushSubscriptionGuard';

import PWABanners from './components/pwa/pwaBanners';

import FirstRunPushPromptController from './components/pushNotifications/firstRunPushPrompt/firstRunPushPromptController';

import OfflineOverlay from './components/pwa/offlineOverlay';

import {handlePossibleConnectionBlock} from './util/connectionRecovery';

import {consumePendingNavigation, setMobileDeviceSeen, setStandaloneSeen} from './util/pushIdb';

import {ensureBackendRegistered, notifyActiveUser} from './util/pushSwSync';

import {getCurrentLanguage, getLanguageFromUser, setUserLanguage} from './i18n/languageService';
import {UI_LANGUAGE_FLEX_KEY} from './i18n/config';
import {userHasSubscription} from './util/utils';
import {requestPageChangePermission} from './util/pageChanger';
import IDate from '@dvrd/idate';

function demoLogin(): boolean {
    const params = new URLSearchParams(location.search);
    if (params.get('demo') === '1') {
        setGaUserId(null);
        return true;
    }
    return false;
}

function getSelectedAccount(user: User): Account | null {
    const {accounts} = user;
    if (accounts.length) {
        const selectedId = getFromLocal(SELECTED_ACCOUNT_KEY) ?? accounts[0].id;
        for (const account of accounts) if (account.id === selectedId) return account;
        return accounts[0];
    }
    return null;
}

// Views reachable from the bottom navigation are mobile-optimized (plus the
// invoice subpages reachable from the order-card menu); every other view shows
// the mobile warning banner on the mobile layout.
// Invoice subpages stack their cards on the mobile layout, dropping the fixed
// 70rem page buffer like the orders view does.
const MOBILE_FLUID_ROUTES = [
    DASHBOARD_ROUTE,
    ORDERS_ROUTE,
    INVOICE_DETAILS_ROUTE,
    EDIT_INVOICE_ROUTE,
    CREDIT_DATA_ROUTE,
    MOBILE_APP_ROUTE,
    CHROME_EXTENSION_ROUTE,
    MCP_SERVER_ROUTE
];

function isMobileReadyView(pathname: string): boolean {
    const normalized = pathname.replace(/\/+$/, '') || '/';
    return MOBILE_READY_ROUTES.some(route => normalized === route || normalized.startsWith(`${route}/`));
}

const devMode = getMode() === 'dev';

export default function App() {
    const {t, i18n} = useTranslation();
    const context = useContext(AppContext);
    const {sidebarRef} = useContext(LayoutContext);
    const {user, validatingUser} = context.userContext;
    const [menuWidth, _setMenuWidth] = useState(remToPx(12.5));
    const [trialBannerClosed, setTrialBannerClosed] = useState(false);
    const stripe = useMemo(() => {
        const stripeApiKey: string = getSetting('stripeApiKey');
        return loadStripe(stripeApiKey);
    }, []);
    const navigate = useNavigate();
    const location = useLocation();
    const bp = useBreakpoint();
    const isMobileLayout = bp.isMobileLayout;
    // Views without a mobile adaptation keep their desktop layout (70rem page buffer)
    // and show the mobile warning banner.
    const isDesktopOnlyView = isMobileLayout && !isMobileReadyView(location.pathname);
    // Fully responsive views opt out of the fixed 70rem page buffer entirely —
    // the notifications page wraps its cards instead of panning sideways. The
    // review-analytics index does the same on the mobile layout, where it
    // reduces to the simplified card feed (feed access required — without it
    // the regular layout renders and keeps the panning buffer, as does the
    // /review-analytics/:ean subview). The orders view stacks its cards on the
    // mobile layout, so it drops the buffer there too.
    // Trailing-slash variants ('/review-analytics/') mount the same index
    // route, so the width gate must agree with what the router renders.
    const normalizedPathname = location.pathname.replace(/\/+$/, '') || '/';
    const isFluidWidthView =
        location.pathname === NOTIFICATIONS_ROUTE ||
        location.pathname.startsWith(`${NOTIFICATIONS_ROUTE}/`) ||
        (isMobileLayout && MOBILE_FLUID_ROUTES.includes(normalizedPathname)) ||
        (isMobileLayout && (normalizedPathname === ADMIN_ROUTE || normalizedPathname.startsWith(`${ADMIN_ROUTE}/`))) ||
        (normalizedPathname === REVIEW_ANALYTICS_ROUTE &&
            showsMobileReviewFeed(context.accountContext.account, isMobileLayout));
    // The Helpscout beacon (a body-level FAB) defaults to the bottom-right corner; on the
    // standalone auth views (login, register) it should sit in the bottom-left corner
    // instead. The account-setup wizard is deliberately NOT in this list: it is a form
    // the user fills top to bottom, and on the left the beacon sat on top of the field
    // labels, their validation messages and the Next button. It keeps the app-wide
    // position, which the rest of the views (invoices, notifications) already use.
    const isBeaconBottomLeftView = [LOGIN_ROUTE, REGISTER_ROUTE].some(
        route => location.pathname === route || location.pathname.startsWith(`${route}/`)
    );
    // The account-setup wizard is a full-screen takeover with its own header and
    // Cancel; the bottom nav must not overlap its footer buttons on mobile.
    const isAccountSetupView =
        location.pathname === SETUP_ACCOUNT_ROUTE || location.pathname.startsWith(`${SETUP_ACCOUNT_ROUTE}/`);
    // Gates the bottom bar AND the header's menu button: the button opens a drawer the
    // bottom bar renders, so it must not appear where that bar is suppressed.
    const showMobileNav = isMobileLayout && !isAccountSetupView;
    useEvent('onLogout', _onLogout);

    // Pending push navigation read from URL on mount. Kept in a ref so it
    // survives StrictMode double-render and is consumed by the validatingUser
    // effect below once the user fetch resolves — that way the mismatch
    // dialog has accurate `user` state to apply the policy against.
    const pendingPushNav = useRef<{
        target: string;
        user_id: string | null;
        account_id: string | null;
        email: string | null;
    } | null>(null);

    // Ref-mirrored applyPushPolicy so the SW-message listener (registered
    // once in a [] effect) always calls the LATEST version with current
    // `user` / `context` state. Without this, the listener captures the
    // very-first-render closure (user=null while validateUser is still
    // running) and the mismatch dialog never fires.
    // Declared HERE (above all function defs) to avoid temporal dead zone
    // when `applyPushPolicyRef.current = applyPushPolicy` runs in render body.
    const applyPushPolicyRef = useRef<
        (target: string, pushUserId: string | null, pushAccountId: string | null, pushEmail: string | null) => void
    >(() => {
        /* no-op until first render assigns the real fn */
    });

    function onClickAdminId(id: string) {
        return function () {
            copyToClipboard(id);
            onShowConfirmSnackbar(t('app.admin.idCopied', {id, defaultValue: '{{id}} gekopieerd'}));
        };
    }

    function getEnvTitle(): string | undefined {
        const mode = getMode();
        if (mode === 'dev') return t('app.env.dev', 'Develop omgeving');
        if (mode === 'accept') return t('app.env.accept', 'Acceptatie omgeving');
        if (['test', 'sub'].includes(mode)) return t('app.env.test', 'Test omgeving');
        return undefined;
    }

    function renderMobileWarningBanner() {
        if (!isDesktopOnlyView) return null;
        const warning = t(
            'app.mobileWarning',
            'Deze pagina is nog niet geoptimaliseerd voor mobiele apparaten. Gebruik hiervoor een laptop of desktop.'
        );
        const sentences = warning.split(/(?<=[.!?])\s+/).filter(Boolean);
        return (
            <InfoBanner className='mobile-banner visible' variant='warning' persistDismiss={false}>
                {sentences.map((sentence, index) => (
                    <span key={index} className='warn-sentence'>
                        {sentence}
                    </span>
                ))}
                <span className='warn-sentence mobile-ready-hint'>
                    {t(
                        'app.mobileReadyHint',
                        "Pagina's die wél goed werken op je telefoon herken je in het menu aan dit label:"
                    )}
                    <BluemateBadge
                        label={<AwesomeIcon name={'mobile-screen-button' as IconName} />}
                        color='green'
                        size='small'
                        className='mobile-ready-badge'
                        tooltip={t('sidebarMenu.mobileFriendly', 'Deze pagina werkt goed op mobiel')}
                    />
                </span>
            </InfoBanner>
        );
    }

    function renderAdminStatsBanner() {
        if (!user || (!hasAdminLogin() && user.type !== UserType.SUPER_ADMIN)) return null;
        const {account} = context.accountContext;
        const apiKey = account?.getAPIKey() ?? null;
        return (
            <InfoBanner className='admin-env-banner' variant='critical' title={getEnvTitle()} persistDismiss>
                <span className='admin-stats-banner'>
                    <span className='env-item'>
                        <label className='key'>{t('app.admin.user', 'Gebruiker')}:</label>{' '}
                        <label onClick={onClickAdminId(user.id)} className='click' title={t('common.copy', 'Kopieren')}>
                            {user.id}
                        </label>
                    </span>
                    {account !== null && (
                        <span className='env-item'>
                            <label className='key'>{t('app.admin.account', 'Account')}:</label>{' '}
                            <label
                                onClick={onClickAdminId(account.id)}
                                className='click'
                                title={t('common.copy', 'Kopieren')}
                            >
                                {account.id}
                            </label>
                        </span>
                    )}
                    {apiKey !== null && (
                        <span className='env-item'>
                            <label className='key'>{t('app.admin.apiKey', 'API key')}:</label>{' '}
                            <label
                                onClick={onClickAdminId(apiKey.id)}
                                className='click'
                                title={t('common.copy', 'Kopieren')}
                            >
                                {apiKey.id}
                            </label>
                        </span>
                    )}
                </span>
            </InfoBanner>
        );
    }

    function onClickApiFlow(evt: React.MouseEvent) {
        sendGaEvent('Click', 'Dashboard', 'API keys instellen');
        const account = context.accountContext.account;
        if (!account) return;
        saveToLocal(SELECTED_ACCOUNT_KEY, account.id);
        onConnectBolOauth(evt.shiftKey);
    }

    function _onLogout(redirect?: string) {
        if (!context.userContext.user) return;
        onGlobalLoad(true);
        logout({
            callback: (data: ResponseData) => {
                onGlobalLoad(false);
                if (data.success) {
                    clearBeaconIds();
                    googleLogout();
                    // Drop the push-handoff login email prefill — it's PII and
                    // must not leak to the next user on a shared / long-lived PWA.
                    deleteFromSession(PUSH_EMAIL_KEY);
                    if (data.logged_in) {
                        // Still logged in as admin
                        onAdminLogout()
                            .then((user: User) => {
                                if (user.accounts.length) saveToLocal(SELECTED_ACCOUNT_KEY, user.accounts[0].id);
                                context.onChangeContext(
                                    {
                                        user,
                                        accounts: user.accounts,
                                        account: user.accounts.length ? user.accounts[0] : null
                                    },
                                    () => {
                                        navigate(ADMIN_USERS_ROUTE);
                                    }
                                );
                            })
                            .catch(() => {
                                // Full logout: clear the impersonation flag so a
                                // subsequent regular login is NOT treated as admin
                                // (it's a module-level flag that otherwise persists
                                // for the whole SPA session, until a full reload).
                                setIsAdminLogin(false);
                                deleteFromLocal(SELECTED_ACCOUNT_KEY);
                                setGaUserId(null);
                                if (redirect !== undefined) {
                                    navigate(redirect);
                                    context.onChangeContext({accounts: [], account: null, user: null});
                                } else {
                                    context.onChangeContext({accounts: [], account: null, user: null}, () =>
                                        navigate(LOGIN_ROUTE)
                                    );
                                }
                            });
                    } else {
                        // Full logout (no admin session left): reset the impersonation
                        // flag so the next regular login behaves as a normal user.
                        setIsAdminLogin(false);
                        deleteFromLocal(SELECTED_ACCOUNT_KEY);
                        setGaUserId(null);
                        if (redirect !== undefined) {
                            navigate(redirect);
                            context.onChangeContext({accounts: [], account: null, user: null});
                        } else {
                            context.onChangeContext({accounts: [], account: null, user: null}, () =>
                                navigate(LOGIN_ROUTE)
                            );
                        }
                    }
                } else showDialog('Het uitloggen is niet gelukt.', 'Uitloggen mislukt');
            }
        });
    }

    /** Auto-switch the selected sales account if the push targets a different
     *  account than the one currently in context. Falls back to silent
     *  navigation when:
     *    - the user has no access to the target account anymore (rare —
     *      account removed since the push was sent),
     *    - we cannot resolve the account from user.accounts.
     *  Returns true if it triggered the navigation itself, false otherwise. */
    function switchAccountAndNavigate(target: string, pushAccountId: string | null): boolean {
        if (!pushAccountId || !user) return false;
        const currentAccount = context.accountContext.account;
        if (currentAccount?.id === pushAccountId) return false;
        const targetAccount = (user.accounts || []).find(a => a.id === pushAccountId);
        if (!targetAccount) return false; // user lost access to that account
        saveToLocal(SELECTED_ACCOUNT_KEY, pushAccountId);
        context.onChangeContext({account: targetAccount}, () => {
            navigate(target);
        });
        return true;
    }

    /** Apply policy for a push-click navigation target+user pair.
     *
     *  No payload user_id   → legacy push, just navigate (with optional account switch).
     *  Logged out           → redirect to /inloggen?redirect=<target>&push_user=<id>.
     *  Match (user.id===id) → optionally switch sales account, then navigate to target.
     *  Mismatch + admin     → dialog "switch to user X?" (calls adminLogin).
     *  Mismatch + user      → dialog "log out and log in?" (logs out, redirects to login). */
    function applyPushPolicy(
        target: string,
        pushUserId: string | null,
        pushAccountId: string | null,
        pushEmail: string | null
    ): void {
        if (!target.startsWith('/') || target.startsWith('//')) return;
        if (!pushUserId) {
            if (switchAccountAndNavigate(target, pushAccountId)) return;
            if (window.location.pathname !== target) navigate(target);
            return;
        }
        if (!user) {
            const params = new URLSearchParams();
            params.set('redirect', target);
            params.set('push_user', pushUserId);
            if (pushAccountId) params.set('push_account', pushAccountId);
            // Pre-fill the login form with the recipient's email.
            if (pushEmail) params.set('push_email', pushEmail);
            navigate(`${LOGIN_ROUTE}?${params.toString()}`);
            return;
        }
        if (user.id === pushUserId) {
            if (switchAccountAndNavigate(target, pushAccountId)) return;
            if (window.location.pathname !== target) navigate(target);
            return;
        }
        // Mismatch — choose policy by user capability. isSupport() covers
        // ADMIN / SUPPORT / SUPER_ADMIN (same predicate the sidebar uses
        // for showing admin tools).
        if (user.isSupport()) {
            showDialog(
                t('push.mismatch.adminBody', 'Deze melding is bedoeld voor een andere gebruiker. Wisselen?'),
                t('push.mismatch.title', 'Andere gebruiker'),
                [
                    t('common.cancel', 'Annuleren'),
                    {
                        label: t('push.mismatch.adminSwitchBtn', 'Wisselen'),
                        onClick: () => doAdminSwitchAndNavigate(pushUserId, target, pushAccountId),
                        primary: true
                    }
                ]
            );
        } else {
            showDialog(
                t(
                    'push.mismatch.userBody',
                    'Deze melding is bedoeld voor een ander account. Uitloggen om in te loggen?'
                ),
                t('push.mismatch.title', 'Andere gebruiker'),
                [
                    t('common.cancel', 'Annuleren'),
                    {
                        label: t('push.mismatch.userLogoutBtn', 'Uitloggen'),
                        onClick: () => doLogoutAndRedirectToLogin(target, pushUserId, pushAccountId, pushEmail),
                        primary: true
                    }
                ]
            );
        }
    }

    // Update the ref on every render so listeners registered in []-deps
    // effects always call the latest applyPushPolicy (with current `user`).
    applyPushPolicyRef.current = applyPushPolicy;

    function doAdminSwitchAndNavigate(pushUserId: string, target: string, pushAccountId: string | null) {
        onGlobalLoad(true);
        adminLoginRequest({
            id: pushUserId,
            callback: (data: ResponseData) => {
                onGlobalLoad(false);
                if (!data.success) {
                    showDialog(
                        t('push.mismatch.switchFailed', 'Wisselen is niet gelukt.'),
                        t('push.mismatch.title', 'Andere gebruiker')
                    );
                    return;
                }
                const newUser = new User(data.user);
                setIsAdminLogin(true);
                // Prefer the push payload's account_id when available, so the
                // notification lands inside the right sales account on a
                // multi-account user. Fall back to the first account.
                let account = null;
                if (pushAccountId) {
                    account = newUser.accounts.find(a => a.id === pushAccountId) ?? null;
                }
                if (!account) {
                    account = newUser.accounts.length ? newUser.accounts[0] : null;
                }
                if (account) saveToLocal(SELECTED_ACCOUNT_KEY, account.id);
                context.onChangeContext({user: newUser, accounts: newUser.accounts, account}, () => navigate(target));
            },
            // `callback` fires only on 2xx — a network failure / non-2xx would
            // otherwise leave the app stuck behind the global loader forever.
            errorCallback: () => {
                onGlobalLoad(false);
                showDialog(
                    t('push.mismatch.switchFailed', 'Wisselen is niet gelukt.'),
                    t('push.mismatch.title', 'Andere gebruiker')
                );
            }
        });
    }

    function doLogoutAndRedirectToLogin(
        target: string,
        pushUserId: string,
        pushAccountId: string | null,
        pushEmail: string | null
    ) {
        onGlobalLoad(true);
        logout({
            callback: (data: ResponseData) => {
                onGlobalLoad(false);
                if (!data.success) {
                    showDialog(
                        t('header.logoutFailedMessage', 'Het uitloggen is niet gelukt.'),
                        t('header.logoutFailed', 'Uitloggen mislukt')
                    );
                    return;
                }
                clearBeaconIds();
                googleLogout();
                // Full logout: clear the impersonation flag so the upcoming
                // login (pre-filled from the push) is handled as a regular user,
                // not as a lingering admin session.
                setIsAdminLogin(false);
                deleteFromLocal(SELECTED_ACCOUNT_KEY);
                setGaUserId(null);
                context.onChangeContext({accounts: [], account: null, user: null}, () => {
                    const params = new URLSearchParams({redirect: target, push_user: pushUserId});
                    if (pushAccountId) params.set('push_account', pushAccountId);
                    if (pushEmail) params.set('push_email', pushEmail);
                    navigate(`${LOGIN_ROUTE}?${params.toString()}`);
                });
            },
            // `callback` fires only on 2xx — a network failure / non-2xx would
            // otherwise leave the app stuck behind the global loader forever.
            errorCallback: () => {
                onGlobalLoad(false);
                showDialog(
                    t('header.logoutFailedMessage', 'Het uitloggen is niet gelukt.'),
                    t('header.logoutFailed', 'Uitloggen mislukt')
                );
            }
        });
    }

    function onClickDemoRegister() {
        if (!user?.isDemoAccount) return;
        onGlobalLoad(true);
        logout({
            callback: (data: ResponseData) => {
                onGlobalLoad(false);
                if (data.success) {
                    deleteFromAppLocal('newUser');
                    deleteFromLocal(SELECTED_ACCOUNT_KEY);
                    setGaUserId(null);
                    context.onChangeContext({accounts: [], account: null, user: null}, () => {
                        window.setTimeout(() => {
                            navigate(REGISTER_ROUTE);
                        });
                    });
                } else
                    showDialog(
                        t('header.logoutFailedMessage', 'Het uitloggen is niet gelukt.'),
                        t('header.logoutFailed', 'Uitloggen mislukt')
                    );
            },
            // `callback` fires only on 2xx — clear the global loader and surface
            // the failure on a network error / non-2xx too.
            errorCallback: () => {
                onGlobalLoad(false);
                showDialog(
                    t('header.logoutFailedMessage', 'Het uitloggen is niet gelukt.'),
                    t('header.logoutFailed', 'Uitloggen mislukt')
                );
            }
        });
    }

    function onClickUpgrade() {
        requestPageChangePermission(() => {
            navigate(SUBSCRIPTION_ROUTE);
        });
    }

    function renderTrialBanner() {
        // Demo accounts get their own banner above; never show the trial banner alongside it.
        if (trialBannerClosed) return null;
        if (!user || user.isDemoAccount || !user.subscription || userHasSubscription(user, false)) return null;
        // The banner's button navigates to the subscription settings page; there the user is already
        // where it points (both the trial-countdown and the trial/subscription-ended variant), so
        // showing it would be redundant.
        if (location.pathname.startsWith(SUBSCRIPTION_ROUTE)) return null;
        const {trialEndDate} = user.subscription;
        const now = IDate.now();
        const showDays = trialEndDate?.isAtOrAfter(now, 'day') ?? false;
        const daysLeft: number = Math.ceil(trialEndDate?.diff(now, 'd', true) ?? 0);
        // More than 7 days left stays calm blue; 7 or fewer turns orange (warning). An ended trial
        // falls back to blue. Never red.
        const variant: 'info' | 'warning' = !showDays || daysLeft > 7 ? 'info' : 'warning';
        const bodyText = showDays
            ? t('header.trialSub', {
                  count: daysLeft,
                  defaultValue:
                      'Nog {{count}} dagen proef te gaan. Als je nu een abonnement start, worden de resterende gratis dagen eerst opgemaakt.'
              })
            : t('header.trialEnded', 'Je proefperiode is verlopen');
        return (
            <InfoBanner
                className='trial-banner none-desktop'
                featureKey='trial'
                variant={variant}
                title={t('header.trialTitle', 'Nu abonnement starten?')}
                actionLabel={t('header.trialButton', 'Abonnement starten...')}
                onAction={onClickUpgrade}
                persistDismiss={false}
                onClose={() => setTrialBannerClosed(true)}
                primaryBannerButton
            >
                {bodyText}
            </InfoBanner>
        );
    }

    function validateUser() {
        if (requireMaintenanceLogin()) {
            context.onChangeContext({validatingUser: false});
            return;
        }
        if (demoLogin()) return startApplication(null);
        User.get(
            null,
            (user: User | null, _success: boolean | undefined, data: ResponseData) => {
                // The session-bound CSRF token rides along with the user payload; every
                // request from here on picks it up in bluemateFetch.
                setCSRF(data.csrf_token ?? '');
                if (!user || (user.isDemoAccount && !devMode)) {
                    return startApplication(null);
                }
                setGaUserId(user.id);
                const account = getSelectedAccount(user);
                setBeaconIds(user, account);
                if (user.isNewUser) saveToAppLocal('newUser', true);
                context.onChangeContext(
                    {
                        user,
                        accounts: user.accounts,
                        account: account
                    },
                    () => {
                        if (account) setSentryUser(user, account);
                        else {
                            setSentryUser(user);
                            directTimeout(onAddAccount);
                        }
                        startApplication(user, account);
                    }
                );
            },
            (response?: Response) => {
                // A request that never arrived is not a logout. Cloudflare answers
                // a client it distrusts (a desktop User-Agent from a mobile-carrier
                // address is enough — "Desktop site" on a phone) with
                // `cf-mitigated: challenge` and an HTML body, and there is no human
                // behind an XHR to solve it. Treating that as "not authenticated"
                // dropped a perfectly valid session on the login screen, which is
                // what users reported as being logged out at random.
                handlePossibleConnectionBlock(response);
                startApplication(null);
            }
        );
    }

    function startApplication(user: User | null, account?: Account | null) {
        if (user && (!user.isDemoAccount || devMode)) {
            if (/inloggen|registreren/.test(window.location.pathname)) {
                const searchParams = new URLSearchParams(location.search);
                const redirect = searchParams.get('redirect');
                const pushAccount = searchParams.get('push_account');
                // `redirect` comes from the URL (incl. the push-click handoff), so it
                // runs through the same validator the logged-out path uses: a
                // same-origin absolute path that matches the app's route table.
                // Anything else falls through to the default route.
                const safeRedirect = isSafeRedirectPath(redirect) ? redirect : null;
                if (safeRedirect) {
                    // If the login URL included push_account (we redirected
                    // here from a push click while logged-out), switch the
                    // selected sales account before navigating so the user
                    // lands inside the right account context.
                    if (pushAccount && user.accounts) {
                        const targetAcct = user.accounts.find(a => a.id === pushAccount);
                        if (targetAcct) {
                            saveToLocal(SELECTED_ACCOUNT_KEY, pushAccount);
                            context.onChangeContext({account: targetAcct}, () => navigate(safeRedirect));
                            context.onChangeContext({validatingUser: false});
                            return;
                        }
                    }
                    navigate(safeRedirect);
                } else {
                    const defaultRoute = getDefaultRoute(account?.userRights, account?.userRole);
                    navigate(defaultRoute);
                }
            }
        }
        context.onChangeContext({validatingUser: false});
    }

    function onSetMenuWidth() {
        const element = sidebarRef.current;
        if (!element) return;
        const width = element.getBoundingClientRect().width;
        _setMenuWidth(width);
    }

    useEffect(() => {
        setMenuWidth(menuWidth);
    }, [menuWidth]);

    useEffect(() => {
        if (user && !menuWidth) window.setTimeout(onSetMenuWidth, 500);
    }, [user]);

    // Move the Helpscout beacon to the bottom-left on auth/setup views (it is injected
    // as a body-level element, so it can only be targeted via a class on <body>).
    useEffect(() => {
        document.body.classList.toggle('beacon-bottom-left', isBeaconBottomLeftView);
        return () => document.body.classList.remove('beacon-bottom-left');
    }, [isBeaconBottomLeftView]);

    const userLanguageFlex: string | null = user?.getFlex(UI_LANGUAGE_FLEX_KEY) ?? null;
    useEffect(() => {
        const backendLanguage = getLanguageFromUser(user);
        const activeLanguage = backendLanguage ?? getCurrentLanguage();
        void i18n.changeLanguage(activeLanguage);
        // Reconcile UI language → backend. When a logged-in user has no
        // persisted `flex.ui_language` yet (e.g. they chose a language before
        // backend persistence existed, so it only lives in localStorage), push
        // the active UI language to the backend. Push notifications are
        // localized server-side from `flex.ui_language`; without this the UI
        // shows EN while push silently stays NL (the server default).
        if (user && !backendLanguage) {
            void setUserLanguage(user, activeLanguage);
        }
    }, [user?.id, userLanguageFlex]);

    // Heartbeat that this user is launching the installed PWA. iOS Safari has no
    // way to detect an installed PWA, but user.flex is shared with the standalone
    // app (same backend user), so Safari reads this to hide the "Add to Home
    // Screen" hint. Stored as a timestamp refreshed on launch (throttled to once
    // per ~12h) — NOT a write-once boolean — so that after an uninstall (which iOS
    // never signals) the value goes stale and the hint returns. Sends flex only,
    // so it won't trigger the Stripe country sync.
    useEffect(() => {
        if (!user || !isStandalone()) return;
        if (!shouldRefreshPwaInstalledHeartbeat(user.getFlex(PWA_INSTALLED_FLEX_KEY))) return;
        const now = Date.now();
        const nextFlex = {...user.flex, [PWA_INSTALLED_FLEX_KEY]: now};
        user.setFlex(PWA_INSTALLED_FLEX_KEY, now);
        updateUser({data: {flex: nextFlex}, callback: () => undefined});
    }, [user?.id]);

    useEffect(() => {
        // Cold-start push handoff: SW appends ?push_target=/path[&push_user=<id>][&push_account=<id>]
        // when it opens the app from a notification click. We DEFER navigation
        // until validateUser() resolves — otherwise we'd race against the user
        // fetch and apply the policy with stale state. Strip the params from
        // the URL synchronously so a refresh does not re-trigger the flow.
        const initialParams = new URLSearchParams(window.location.search);
        const pushTarget = initialParams.get('push_target');
        const pushUser = initialParams.get('push_user');
        const pushAccount = initialParams.get('push_account');
        const pushEmail = initialParams.get('push_email');
        if (pushTarget && pushTarget.startsWith('/') && !pushTarget.startsWith('//')) {
            pendingPushNav.current = {
                target: pushTarget,
                user_id: pushUser,
                account_id: pushAccount,
                email: pushEmail
            };
            initialParams.delete('push_target');
            initialParams.delete('push_user');
            initialParams.delete('push_account');
            initialParams.delete('push_email');
            const remainingQuery = initialParams.toString();
            const cleaned =
                window.location.pathname + (remainingQuery ? '?' + remainingQuery : '') + window.location.hash;
            window.history.replaceState({}, '', cleaned);
        }

        validateUser();
        startIdleObserver();
        // PWA: register SW (prod-only guard is inside the helper) and capture the install prompt.
        void registerServiceWorker();
        const unregisterInstall = setupInstallPrompt();

        let badgeTimer: ReturnType<typeof setTimeout> | undefined;
        function clearBadgeOnFocus() {
            if (document.visibilityState !== 'visible') return;
            clearTimeout(badgeTimer);
            badgeTimer = setTimeout(() => {
                // Close stale OS notification toasts and let the SW reconcile the
                // app-icon badge to the real unread total. We no longer clear the
                // badge here: unread now persists per view until the user opens
                // that view (NotificationBadgeProvider drives the per-view badges
                // and the app-icon total).
                navigator.serviceWorker?.controller?.postMessage({type: 'CLEAR_BADGE'});
            }, 500);
        }
        document.addEventListener('visibilitychange', clearBadgeOnFocus);

        // Service-worker click handoff:
        //   NAVIGATE             — match case (or no payload user_id). Just navigate.
        //   PUSH_CLICK_MISMATCH  — warm-app + mismatch. Apply dialog policy.
        // Indirection via ref guarantees we always use the LATEST applyPushPolicy
        // (with current user state) even though this listener is registered
        // once at mount when user was still loading.
        function onSwMessage(event: MessageEvent) {
            if (!event.data || typeof event.data !== 'object') return;
            const url = typeof event.data.url === 'string' ? event.data.url : null;
            const pushUserId = typeof event.data.user_id === 'string' ? event.data.user_id : null;
            const pushAccountId = typeof event.data.account_id === 'string' ? event.data.account_id : null;
            const pushEmail = typeof event.data.email === 'string' ? event.data.email : null;
            if (event.data.type === 'NAVIGATE' && url && url.startsWith('/')) {
                applyPushPolicyRef.current(url, pushUserId, pushAccountId, pushEmail);
            } else if (event.data.type === 'PUSH_CLICK_MISMATCH' && url && url.startsWith('/')) {
                applyPushPolicyRef.current(url, pushUserId, pushAccountId, pushEmail);
            }
        }
        navigator.serviceWorker?.addEventListener('message', onSwMessage);

        return function () {
            clearTimeout(badgeTimer);
            stopIdleObserver();
            unregisterInstall();
            document.removeEventListener('visibilitychange', clearBadgeOnFocus);
            navigator.serviceWorker?.removeEventListener('message', onSwMessage);
        };
    }, []);

    // Process any push handoff once validateUser has resolved. We deferred
    // applying the policy until now because applyPushPolicy needs accurate
    // `user` state (otherwise a cold start would race the user fetch and
    // always fall into the "logged out" branch).
    useEffect(() => {
        if (validatingUser) return;
        const pending = pendingPushNav.current;
        if (pending) {
            pendingPushNav.current = null;
            applyPushPolicy(pending.target, pending.user_id, pending.account_id, pending.email);
            return;
        }
        // IDB fallback for the edge case where the installed PWA opened at
        // start_url (`/`) instead of the URL passed to openWindow — the SW
        // wrote pending_nav (target + user + account) before openWindow, so we
        // can still recover the full context, including the sales account to
        // switch to.
        void consumePendingNavigation().then(entry => {
            // IDB pending-nav fallback does not carry the email, so the login form
            // simply won't be pre-filled on this rare start_url path.
            if (entry) applyPushPolicy(entry.target, entry.user_id, entry.account_id, null);
        });
    }, [validatingUser]);

    // Sync active user to the SW. The SW reads this on notificationclick to
    // detect payload-vs-active mismatches. Also re-register the browser's
    // push subscription under the now-active user (multi-user invariant:
    // each user that has ever logged in keeps a DeviceToken row pointing
    // at the same endpoint, so the backend can deliver pushes for any of
    // them to this browser).
    const currentAccountId = context.accountContext.account?.id ?? null;
    useEffect(() => {
        if (validatingUser) return;
        // Pass fullName so the SW push handler can prefix multi-account
        // notifications with a friendly label (e.g. "Anna Kowalski: <body>").
        const displayName = user?.fullName || user?.email || undefined;
        void notifyActiveUser(user?.id || null, displayName);
        if (user?.id && currentAccountId) void ensureBackendRegistered(user.id, currentAccountId);
    }, [user?.id, currentAccountId, validatingUser]);

    // Every STANDALONE launch: (a) persist the local installed flag, so the
    // browser tab (and the uninstall guard) recognizes the install even when
    // it predates the `appinstalled` listener or came from a browser menu
    // that never fires the event; (b) mirror the fact to IDB for the SW's
    // pushsubscriptionchange gate (mobile rotations re-register only when the
    // installed app has actually been launched on this device).
    useEffect(() => {
        if (!isStandalone()) return;
        markPwaInstalledLocally();
        void setStandaloneSeen();
    }, []);

    // Hand the SW our hardware verdict on "is this a phone/tablet": it has no
    // matchMedia and no maxTouchPoints, so its own pushsubscriptionchange gate
    // can only sniff the user-agent — which the browser's desktop-site toggle
    // rewrites. Written from the browser tab as well as the installed app
    // (Android shares this IDB between Chrome and the WebAPK).
    useEffect(() => {
        if (isTouchFirstDevice()) void setMobileDeviceSeen();
    }, []);

    // Mobile push-subscription guard: on every load — and again whenever the
    // browser fires beforeinstallprompt (a definitive "PWA not installed"
    // signal) — remove a push subscription that lives in a MOBILE BROWSER
    // while the app is not installed on this device (e.g. left behind by an
    // uninstall; on Android the browser keeps receiving its pushes).
    // Detection lives in the pwaInstallState service, independent of the
    // install banners.
    useEffect(() => {
        if (validatingUser || !user?.id || !currentAccountId) return;
        const runGuard = () => void cleanupUninstalledPwaSubscription(user.id, currentAccountId);
        runGuard();
        window.addEventListener(PWA_INSTALL_AVAILABLE_EVENT, runGuard);
        return () => window.removeEventListener(PWA_INSTALL_AVAILABLE_EVENT, runGuard);
    }, [user?.id, currentAccountId, validatingUser]);

    if (validatingUser)
        return (
            <>
                <Loader active />
                <AppNavigator />
            </>
        );
    return (
        <GoogleOAuthProvider clientId='85081984902-vov3r4rckqdh843l7pf8r8bdsbqqhe7u'>
            <AppNavigator />
            <OfflineOverlay />
            {!!user && !isBareRoutePath(location.pathname) ? (
                <NotificationBadgeProvider>
                    <MobileNavProvider available={showMobileNav}>
                        <HeaderController menuWidth={menuWidth} />
                        <div className='page-content'>
                            {!isMobileLayout && <SidebarMenuController />}
                            <Elements stripe={stripe}>
                                <BluemateContent
                                    className={
                                        classNames(
                                            isDesktopOnlyView && 'desktop-only-view',
                                            isFluidWidthView && 'fluid-width'
                                        ) || undefined
                                    }
                                >
                                    <APIConnectionBar onClickApiFlow={onClickApiFlow} />
                                    {renderAdminStatsBanner()}
                                    {renderMobileWarningBanner()}
                                    <PWABanners />
                                    <FirstRunPushPromptController />
                                    {user?.isDemoAccount && (
                                        <InfoBanner
                                            className='demo-trial-banner'
                                            variant='info'
                                            title={t('header.demoAccount', 'Dit is een demo account')}
                                            actionLabel={t('header.demoButton', 'Nu Registeren')}
                                            onAction={onClickDemoRegister}
                                            persistDismiss={false}
                                            primaryBannerButton
                                        >
                                            {t(
                                                'header.demoSub',
                                                'Nu gratis 30 dagen proberen? Geen betaalmethode nodig. Koppel nu jouw eigen bol accounts.'
                                            )}
                                        </InfoBanner>
                                    )}
                                    {renderTrialBanner()}
                                    <Outlet />
                                </BluemateContent>
                            </Elements>
                        </div>
                        <PaywallErrorBoundary resetKey={location.pathname}>
                            <TrialEndController />
                        </PaywallErrorBoundary>
                        {showMobileNav && <BottomNavBar />}
                    </MobileNavProvider>
                </NotificationBadgeProvider>
            ) : (
                <BluemateContent className='no-buffer'>
                    <Outlet />
                </BluemateContent>
            )}
            <URLActions />
            <OverviewApiUrlSync />
            <PopupController />
        </GoogleOAuthProvider>
    );
}
